import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewEncapsulation, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Point, Offset, RectangleFactory, GeometryModule, PlacementService } from '@eui/components/legacy/common/geometry';
import { debounceTime } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2 from '@eui/core';
import * as i4 from '@eui/components/eui-icon';
import { EuiIconModule } from '@eui/components/eui-icon';

const constants = {
    offset: 15,
    directionClass: {
        none: 'no-direction',
        left: 'left',
        top: 'top',
        right: 'right',
        bottom: 'bottom',
    }
};

var PopOverState;
(function (PopOverState) {
    PopOverState[PopOverState["OPEN"] = 0] = "OPEN";
    PopOverState[PopOverState["CLOSED"] = 1] = "CLOSED";
})(PopOverState || (PopOverState = {}));
class PopoverVM {
    constructor(placementClassModifier = 'no-direction', popoverPosition = Point.create(0, 0), arrowOffset = Offset.create(0, 0)) {
        this.placementClassModifier = placementClassModifier;
        this.popoverPosition = popoverPosition;
        this.arrowOffset = arrowOffset;
    }
    static create(popoverDetails) {
        if (!popoverDetails) {
            return new PopoverVM();
        }
        return new PopoverVM(popoverDetails.placementClassModifier, popoverDetails.popoverPosition, popoverDetails.arrowOffset);
    }
}

class RightPlacementStrategy {
    constructor(placementService) {
        this.placementService = placementService;
    }
    getId() {
        return 'right';
    }
    calculate(placementOptions) {
        const positionedPopoverRect = this.placementService.place({
            anchor: placementOptions.anchorRect,
            element: placementOptions.popoverRect,
            placementId: this.getId(),
            offsetAlong: constants.offset
        });
        const arrowOffset = getArrowOffset$1(placementOptions.anchorRect, positionedPopoverRect);
        const placementClassModifier = getPlacementClassModifier$1(placementOptions.anchorRect, positionedPopoverRect, placementOptions.arrowRect);
        const popoverPosition = positionedPopoverRect.position();
        return PopoverVM.create({
            popoverPosition, arrowOffset, placementClassModifier
        });
    }
}
function getArrowOffset$1(anchorRect, popoverRect) {
    const offsetX = 0;
    const offsetY = anchorRect.center().y - popoverRect.center().y;
    return Offset.create(offsetX, offsetY);
}
function getMaxArrowOffset$1(popoverRect, arrowRect) {
    return (popoverRect.height - arrowRect.height) / 2;
}
function isArrowTooFar$1(anchorRect, popoverRect, arrowRect) {
    const arrowOffset = getArrowOffset$1(anchorRect, popoverRect);
    const maxOffset = getMaxArrowOffset$1(popoverRect, arrowRect);
    const isTooFar = Math.abs(arrowOffset.y) >= maxOffset;
    return isTooFar;
}
function getPlacementClassModifier$1(anchorRect, popoverRect, arrowRect) {
    const isFlip = isFlipped$1(anchorRect, popoverRect);
    const isTooFar = isArrowTooFar$1(anchorRect, popoverRect, arrowRect);
    if (isTooFar) {
        return constants.directionClass.none;
    }
    return isFlip ? constants.directionClass.left : constants.directionClass.right;
}
function isFlipped$1(anchorRect, popoverRect) {
    return popoverRect.isOnTheLeft(anchorRect.center());
}

class LeftPlacementStrategy extends RightPlacementStrategy {
    getId() {
        return 'left';
    }
}

class BottomPlacementStrategy {
    constructor(placementService) {
        this.placementService = placementService;
    }
    getId() {
        return 'bottom';
    }
    calculate(placementOptions) {
        const positionedPopoverRect = this.placementService.place({
            anchor: placementOptions.anchorRect,
            element: placementOptions.popoverRect,
            placementId: this.getId(),
            offsetAlong: constants.offset
        });
        const arrowOffset = getArrowOffset(placementOptions.anchorRect, positionedPopoverRect);
        const placementClassModifier = getPlacementClassModifier(placementOptions.anchorRect, positionedPopoverRect, placementOptions.arrowRect);
        const popoverPosition = positionedPopoverRect.position();
        return PopoverVM.create({
            popoverPosition, arrowOffset, placementClassModifier
        });
    }
}
function getMaxArrowOffset(popoverRect, arrowRect) {
    return (popoverRect.width - arrowRect.width) / 2;
}
function getArrowOffset(anchorRect, popoverRect) {
    const offsetX = anchorRect.center().x - popoverRect.center().x;
    const offsetY = 0;
    return Offset.create(offsetX, offsetY);
}
function isArrowTooFar(anchorRect, popoverRect, arrowRect) {
    const arrowOffset = getArrowOffset(anchorRect, popoverRect);
    const maxOffset = getMaxArrowOffset(popoverRect, arrowRect);
    const isTooFar = Math.abs(arrowOffset.x) >= maxOffset;
    return isTooFar;
}
function isFlipped(anchorRect, popoverRect) {
    return popoverRect.isAbove(anchorRect.center());
}
function getPlacementClassModifier(anchorRect, popoverRect, arrowRect) {
    const isFlip = isFlipped(anchorRect, popoverRect);
    const isTooFar = isArrowTooFar(anchorRect, popoverRect, arrowRect);
    if (isTooFar) {
        return constants.directionClass.none;
    }
    return isFlip ? constants.directionClass.top : constants.directionClass.bottom;
}

class TopPlacementStrategy extends BottomPlacementStrategy {
    getId() {
        return 'top';
    }
}

class PopoverService {
    constructor() {
        this.placementStrategies = [];
    }
    static pickPlacementStrategy(placementStrategies, placementId) {
        function matchStrategy(strategy) {
            return strategy.getId() === placementId;
        }
        return (placementStrategies || []).find(matchStrategy);
    }
    calculate(placementOptions) {
        const placementStrategy = PopoverService.pickPlacementStrategy(this.placementStrategies, placementOptions.placement);
        if (!placementStrategy) {
            throw new Error('Placement not supported: ' + placementOptions.placement);
        }
        return placementStrategy.calculate(placementOptions);
    }
}
/** @nocollapse */ PopoverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: PopoverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ PopoverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: PopoverService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: PopoverService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class UxPopoverComponent {
    constructor(popoverService, renderer, cd, asService) {
        this.popoverService = popoverService;
        this.renderer = renderer;
        this.cd = cd;
        this.asService = asService;
        this.title = '';
        this._showCloseIcon = true;
        this.type = 'default';
        this.placement = 'bottom';
        this.size = 'default';
        this._hasNoContentPadding = false;
        this._isDismissable = true;
        this.e2eAttr = 'ux-popover';
        this.status = new EventEmitter();
        this.isVisible = false;
        this.vm = PopoverVM.create();
    }
    get showCloseIcon() { return this._showCloseIcon; }
    set showCloseIcon(value) { this._showCloseIcon = coerceBooleanProperty(value); }
    get hasNoContentPadding() { return this._hasNoContentPadding; }
    set hasNoContentPadding(value) { this._hasNoContentPadding = coerceBooleanProperty(value); }
    get isDismissable() { return this._isDismissable; }
    set isDismissable(value) { this._isDismissable = coerceBooleanProperty(value); }
    ngOnInit() {
        this.handleWindowResize();
        this.handleDocumentEvents();
    }
    ngAfterViewInit() {
        this.renderer.appendChild(document.body, this.popoverEl.nativeElement);
    }
    handleWindowResize() {
        this.windowResizedSubscription = this.asService.breakpoint$
            .pipe(debounceTime(300))
            .subscribe(bkp => {
            if (this.lastTriggerElement) {
                this.reposition(this.lastTriggerElement);
            }
        });
    }
    handleDocumentEvents() {
        if (this.isDismissable) {
            this.documentClickListener = this.renderer.listen('document', 'click', (evt) => {
                if (!this.popoverEl.nativeElement.contains(evt.target)) {
                    this.hide(evt);
                }
                this.cd.markForCheck();
            });
        }
        this.documentKeyupListener = this.renderer.listen('document', 'keyup', (evt) => {
            if (evt.keyCode === 27) { // 27 = escape
                this.hide(evt);
            }
        });
    }
    onCloseClick(evt) {
        this.hide(evt);
    }
    toggle(event) {
        const triggerElement = event.target;
        if (this.lastTriggerElement === triggerElement) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            this.isVisible ? this.hide(event) : this.show(event);
        }
        else {
            this.show(event);
        }
        this.lastTriggerElement = triggerElement;
    }
    reposition(element) {
        const anchorRect = RectangleFactory.fromHtmlElement(element);
        const popoverRect = RectangleFactory.fromHtmlElement(this.popoverEl.nativeElement);
        const arrowRect = RectangleFactory.fromHtmlElement(this.arrowEl.nativeElement);
        const placement = this.placement;
        this.vm = this.popoverService.calculate({
            placement,
            anchorRect,
            popoverRect,
            arrowRect,
        });
    }
    show(event) {
        // makes sure that this is called after document click event
        setTimeout(() => {
            this.reposition(event.target);
            this.isVisible = true;
            this.cd.markForCheck();
            this.status.emit(PopOverState.OPEN);
        });
    }
    hide(event) {
        this.isVisible = false;
        this.status.emit(PopOverState.CLOSED);
    }
    ngOnDestroy() {
        if (this.documentClickListener) {
            this.documentClickListener();
        }
        if (this.documentKeyupListener) {
            this.documentKeyupListener();
        }
        if (this.windowResizedSubscription) {
            this.windowResizedSubscription.unsubscribe();
        }
        if (this.popoverEl) {
            try {
                this.renderer.removeChild(document.body, this.popoverEl.nativeElement);
            }
            catch (e) {
            }
        }
    }
}
/** @nocollapse */ UxPopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponent, deps: [{ token: PopoverService }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i2.UxAppShellService }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ UxPopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: UxPopoverComponent, selector: "ux-popover", inputs: { title: "title", showCloseIcon: "showCloseIcon", type: "type", placement: "placement", size: "size", hasNoContentPadding: "hasNoContentPadding", isDismissable: "isDismissable", headerStyleClass: "headerStyleClass", e2eAttr: "e2eAttr" }, outputs: { status: "status" }, viewQueries: [{ propertyName: "popoverEl", first: true, predicate: ["popoverContainer"], descendants: true }, { propertyName: "arrowEl", first: true, predicate: ["popoverArrow"], descendants: true }], ngImport: i0, template: "<div #popoverContainer\n     class=\"ux-popover ux-popover--{{ vm.placementClassModifier }} ux-popover--{{ type }} ux-popover--size-{{ size }}\"\n     [class.ux-popover--visible]=\"isVisible\"\n     [style.left]=\"vm.popoverPosition.x + 'px'\"\n     [style.top]=\"vm.popoverPosition.y + 'px'\"\n     attr.data-e2e=\"{{ e2eAttr }}\">\n    <div #popoverArrow class=\"ux-popover__arrow\"\n         [style.marginLeft]=\"vm.arrowOffset.x + 'px'\"\n         [style.marginTop]=\"vm.arrowOffset.y + 'px'\">\n        <div class=\"ux-popover__arrow-inner\"></div>\n    </div>\n    <div *ngIf=\"title\" class=\"ux-popover__header {{ headerStyleClass }}\">\n        <div class=\"ux-popover__title\">{{ title }}</div>\n        <div *ngIf=\"showCloseIcon\"\n             (click)=\"onCloseClick($event)\"\n             class=\"ux-popover__close\">\n             <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n        </div>\n    </div>\n    <div class=\"ux-popover__content\"\n         [class.p-0]=\"hasNoContentPadding\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".ux-popover{background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-15);border-radius:var(--eui-border-radius-base);box-shadow:var(--eui-base-shadow-3);max-width:23.4375rem;position:absolute;visibility:hidden;z-index:var(--eui-base-z-index-max)}.ux-popover__header{border-bottom:1px solid var(--eui-base-color-grey-15);padding:var(--eui-base-spacing-s)}.ux-popover__title{color:var(--eui-base-color-text);font-size:var(--eui-base-font-size-s);font-weight:700;line-height:1;padding-right:var(--eui-base-spacing-s);text-transform:uppercase;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ux-popover__close{color:var(--eui-base-color-grey-25);cursor:pointer;position:absolute;right:0;top:0;transform:translate(-33%,33%)}.ux-popover__close:hover{color:var(--eui-base-color-danger-100)}.ux-popover__content{font-size:var(--eui-base-font-size-s);max-width:100vw;overflow:hidden;padding:var(--eui-base-spacing-s);word-break:break-word;word-wrap:break-word}.ux-popover__arrow{border-color:inherit;border-style:solid;border-width:8px;display:none;height:0;position:absolute;width:0}.ux-popover__arrow-inner{border-color:var(--eui-base-color-white);border-style:solid;border-width:7px;content:\" \";display:block;height:0;position:absolute;width:0}.ux-popover--visible{visibility:visible}.ux-popover--size-small{max-width:12.5rem}.ux-popover--size-large{max-width:34.375rem}.ux-popover--size-auto{max-width:none}.ux-popover--flat .ux-popover__header{border-bottom:none;padding-bottom:0}.ux-popover--no-direction .ux-popover__arrow{display:block;visibility:hidden}.ux-popover--top .ux-popover__arrow,.ux-popover--bottom .ux-popover__arrow,.ux-popover--left .ux-popover__arrow,.ux-popover--right .ux-popover__arrow{display:block}.ux-popover--top .ux-popover__arrow,.ux-popover--bottom .ux-popover__arrow{border-left-color:transparent;border-right-color:transparent;left:50%;transform:translate(-8px)}.ux-popover--top .ux-popover__arrow-inner,.ux-popover--bottom .ux-popover__arrow-inner{border-left-color:transparent;border-right-color:transparent;margin-left:-7px}.ux-popover--top .ux-popover__arrow{border-bottom-width:0;bottom:-8px}.ux-popover--top .ux-popover__arrow-inner{border-bottom-width:0;bottom:1px}.ux-popover--bottom .ux-popover__arrow{border-top-width:0;top:-8px}.ux-popover--bottom .ux-popover__arrow-inner{border-top-width:0;top:1px}.ux-popover--left .ux-popover__arrow,.ux-popover--right .ux-popover__arrow{border-bottom-color:transparent;border-top-color:transparent;top:50%;transform:translateY(-8px)}.ux-popover--left .ux-popover__arrow-inner,.ux-popover--right .ux-popover__arrow-inner{border-bottom-color:transparent;border-top-color:transparent;top:-7px}.ux-popover--left .ux-popover__arrow{border-right-width:0;right:-8px}.ux-popover--left .ux-popover__arrow-inner{border-right-width:0;right:1px}.ux-popover--right .ux-popover__arrow{border-left-width:0;left:-8px}.ux-popover--right .ux-popover__arrow-inner{border-left-width:0;border-right-color:var(--eui-base-color-white);left:1px}.ux-popover--colored-header{background:none;border:none;border-color:var(--eui-base-color-grey-90)}.ux-popover--colored-header .ux-popover__header{background-color:var(--eui-base-color-grey-90);border:0;border-top-left-radius:var(--eui-border-radius-base);border-top-right-radius:var(--eui-border-radius-base)}.ux-popover--colored-header .ux-popover__title,.ux-popover--colored-header .ux-popover__close{color:var(--eui-base-color-white)}.ux-popover--colored-header .ux-popover__content{background-color:var(--eui-base-color-white);border-color:inherit;border-style:solid;border-top:0;border-width:2px;border-bottom-left-radius:var(--eui-border-radius-base);border-bottom-right-radius:var(--eui-border-radius-base)}.ux-popover--colored-header.ux-popover--left .ux-popover__arrow-inner{right:3px}.ux-popover--colored-header.ux-popover--right .ux-popover__arrow-inner{left:3px}.ux-popover--colored-header.ux-popover--top .ux-popover__arrow-inner{bottom:3px}.ux-popover--colored-header.ux-popover--bottom .ux-popover__arrow-inner{border:0}.ux-popover--colored-solid{background-color:var(--eui-base-color-grey-90);border-color:var(--eui-base-color-grey-90);border-style:none}.ux-popover--colored-solid .ux-popover__header{border-bottom-color:var(--eui-base-color-grey-5)}.ux-popover--colored-solid .ux-popover__title,.ux-popover--colored-solid .ux-popover__close,.ux-popover--colored-solid .ux-popover__content{color:var(--eui-base-color-white)}.ux-popover--colored-solid .ux-popover__arrow-inner{border:none}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EuiIconSvgComponent, selector: "eui-icon-svg, span[euiIconSvg], i[euiIconSvg]", inputs: ["icon", "size", "fillColor", "set", "ariaLabelledby", "role", "style", "iconUrl", "aria-label", "ariaHidden", "focusable", "isLoading"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ux-popover', encapsulation: ViewEncapsulation.None, template: "<div #popoverContainer\n     class=\"ux-popover ux-popover--{{ vm.placementClassModifier }} ux-popover--{{ type }} ux-popover--size-{{ size }}\"\n     [class.ux-popover--visible]=\"isVisible\"\n     [style.left]=\"vm.popoverPosition.x + 'px'\"\n     [style.top]=\"vm.popoverPosition.y + 'px'\"\n     attr.data-e2e=\"{{ e2eAttr }}\">\n    <div #popoverArrow class=\"ux-popover__arrow\"\n         [style.marginLeft]=\"vm.arrowOffset.x + 'px'\"\n         [style.marginTop]=\"vm.arrowOffset.y + 'px'\">\n        <div class=\"ux-popover__arrow-inner\"></div>\n    </div>\n    <div *ngIf=\"title\" class=\"ux-popover__header {{ headerStyleClass }}\">\n        <div class=\"ux-popover__title\">{{ title }}</div>\n        <div *ngIf=\"showCloseIcon\"\n             (click)=\"onCloseClick($event)\"\n             class=\"ux-popover__close\">\n             <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n        </div>\n    </div>\n    <div class=\"ux-popover__content\"\n         [class.p-0]=\"hasNoContentPadding\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".ux-popover{background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-15);border-radius:var(--eui-border-radius-base);box-shadow:var(--eui-base-shadow-3);max-width:23.4375rem;position:absolute;visibility:hidden;z-index:var(--eui-base-z-index-max)}.ux-popover__header{border-bottom:1px solid var(--eui-base-color-grey-15);padding:var(--eui-base-spacing-s)}.ux-popover__title{color:var(--eui-base-color-text);font-size:var(--eui-base-font-size-s);font-weight:700;line-height:1;padding-right:var(--eui-base-spacing-s);text-transform:uppercase;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ux-popover__close{color:var(--eui-base-color-grey-25);cursor:pointer;position:absolute;right:0;top:0;transform:translate(-33%,33%)}.ux-popover__close:hover{color:var(--eui-base-color-danger-100)}.ux-popover__content{font-size:var(--eui-base-font-size-s);max-width:100vw;overflow:hidden;padding:var(--eui-base-spacing-s);word-break:break-word;word-wrap:break-word}.ux-popover__arrow{border-color:inherit;border-style:solid;border-width:8px;display:none;height:0;position:absolute;width:0}.ux-popover__arrow-inner{border-color:var(--eui-base-color-white);border-style:solid;border-width:7px;content:\" \";display:block;height:0;position:absolute;width:0}.ux-popover--visible{visibility:visible}.ux-popover--size-small{max-width:12.5rem}.ux-popover--size-large{max-width:34.375rem}.ux-popover--size-auto{max-width:none}.ux-popover--flat .ux-popover__header{border-bottom:none;padding-bottom:0}.ux-popover--no-direction .ux-popover__arrow{display:block;visibility:hidden}.ux-popover--top .ux-popover__arrow,.ux-popover--bottom .ux-popover__arrow,.ux-popover--left .ux-popover__arrow,.ux-popover--right .ux-popover__arrow{display:block}.ux-popover--top .ux-popover__arrow,.ux-popover--bottom .ux-popover__arrow{border-left-color:transparent;border-right-color:transparent;left:50%;transform:translate(-8px)}.ux-popover--top .ux-popover__arrow-inner,.ux-popover--bottom .ux-popover__arrow-inner{border-left-color:transparent;border-right-color:transparent;margin-left:-7px}.ux-popover--top .ux-popover__arrow{border-bottom-width:0;bottom:-8px}.ux-popover--top .ux-popover__arrow-inner{border-bottom-width:0;bottom:1px}.ux-popover--bottom .ux-popover__arrow{border-top-width:0;top:-8px}.ux-popover--bottom .ux-popover__arrow-inner{border-top-width:0;top:1px}.ux-popover--left .ux-popover__arrow,.ux-popover--right .ux-popover__arrow{border-bottom-color:transparent;border-top-color:transparent;top:50%;transform:translateY(-8px)}.ux-popover--left .ux-popover__arrow-inner,.ux-popover--right .ux-popover__arrow-inner{border-bottom-color:transparent;border-top-color:transparent;top:-7px}.ux-popover--left .ux-popover__arrow{border-right-width:0;right:-8px}.ux-popover--left .ux-popover__arrow-inner{border-right-width:0;right:1px}.ux-popover--right .ux-popover__arrow{border-left-width:0;left:-8px}.ux-popover--right .ux-popover__arrow-inner{border-left-width:0;border-right-color:var(--eui-base-color-white);left:1px}.ux-popover--colored-header{background:none;border:none;border-color:var(--eui-base-color-grey-90)}.ux-popover--colored-header .ux-popover__header{background-color:var(--eui-base-color-grey-90);border:0;border-top-left-radius:var(--eui-border-radius-base);border-top-right-radius:var(--eui-border-radius-base)}.ux-popover--colored-header .ux-popover__title,.ux-popover--colored-header .ux-popover__close{color:var(--eui-base-color-white)}.ux-popover--colored-header .ux-popover__content{background-color:var(--eui-base-color-white);border-color:inherit;border-style:solid;border-top:0;border-width:2px;border-bottom-left-radius:var(--eui-border-radius-base);border-bottom-right-radius:var(--eui-border-radius-base)}.ux-popover--colored-header.ux-popover--left .ux-popover__arrow-inner{right:3px}.ux-popover--colored-header.ux-popover--right .ux-popover__arrow-inner{left:3px}.ux-popover--colored-header.ux-popover--top .ux-popover__arrow-inner{bottom:3px}.ux-popover--colored-header.ux-popover--bottom .ux-popover__arrow-inner{border:0}.ux-popover--colored-solid{background-color:var(--eui-base-color-grey-90);border-color:var(--eui-base-color-grey-90);border-style:none}.ux-popover--colored-solid .ux-popover__header{border-bottom-color:var(--eui-base-color-grey-5)}.ux-popover--colored-solid .ux-popover__title,.ux-popover--colored-solid .ux-popover__close,.ux-popover--colored-solid .ux-popover__content{color:var(--eui-base-color-white)}.ux-popover--colored-solid .ux-popover__arrow-inner{border:none}\n"] }]
        }], ctorParameters: function () { return [{ type: PopoverService }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i2.UxAppShellService }]; }, propDecorators: { popoverEl: [{
                type: ViewChild,
                args: ['popoverContainer']
            }], arrowEl: [{
                type: ViewChild,
                args: ['popoverArrow']
            }], title: [{
                type: Input
            }], showCloseIcon: [{
                type: Input
            }], type: [{
                type: Input
            }], placement: [{
                type: Input
            }], size: [{
                type: Input
            }], hasNoContentPadding: [{
                type: Input
            }], isDismissable: [{
                type: Input
            }], headerStyleClass: [{
                type: Input
            }], e2eAttr: [{
                type: Input
            }], status: [{
                type: Output
            }] } });

function placementServiceFactory(placementService) {
    const popoverService = new PopoverService();
    popoverService.placementStrategies = [
        new LeftPlacementStrategy(placementService),
        new TopPlacementStrategy(placementService),
        new RightPlacementStrategy(placementService),
        new BottomPlacementStrategy(placementService),
    ];
    return popoverService;
}
class UxPopoverComponentModule {
}
/** @nocollapse */ UxPopoverComponentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ UxPopoverComponentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponentModule, declarations: [UxPopoverComponent], imports: [CommonModule, GeometryModule, EuiIconModule], exports: [UxPopoverComponent] });
/** @nocollapse */ UxPopoverComponentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponentModule, providers: [
        {
            provide: PopoverService,
            useFactory: placementServiceFactory,
            deps: [PlacementService],
        },
    ], imports: [CommonModule, GeometryModule, EuiIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxPopoverComponentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, GeometryModule, EuiIconModule],
                    exports: [UxPopoverComponent],
                    declarations: [UxPopoverComponent],
                    providers: [
                        {
                            provide: PopoverService,
                            useFactory: placementServiceFactory,
                            deps: [PlacementService],
                        },
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { UxPopoverComponent, UxPopoverComponentModule, placementServiceFactory };
